import { useCallback } from "react";
import { Heart } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAddFavorite,
  useRemoveFavorite,
  getListFavoritesQueryKey,
  getListRecipesQueryKey,
  getGetRecipeQueryKey,
} from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface FavoriteButtonProps {
  recipeId: number;
  isFavorite: boolean;
  /** "card" — small overlay heart for cards; "detail" — labeled pill for detail page */
  variant?: "card" | "detail";
  className?: string;
}

export function FavoriteButton({
  recipeId,
  isFavorite,
  variant = "card",
  className,
}: FavoriteButtonProps) {
  const qc = useQueryClient();
  const { toast } = useToast();

  // Optimistically flip the cached `isFavorite` flag everywhere it appears
  // (list and detail caches), then refetch the favorites list so the
  // /favorites page stays in sync. We avoid invalidating the recipe lists
  // because their nutrition/ingredients payloads are heavy and the only
  // field that changed is `isFavorite`.
  const flipCachedFlag = useCallback(
    (next: boolean) => {
      const update = (r: { id: number; isFavorite?: boolean | null } | undefined) =>
        r && r.id === recipeId ? { ...r, isFavorite: next } : r;

      qc.setQueriesData<{ recipes: Array<{ id: number; isFavorite?: boolean | null }> }>(
        { queryKey: getListRecipesQueryKey() },
        (old) =>
          old
            ? {
                ...old,
                recipes: old.recipes.map((r) => update(r) as typeof r),
              }
            : old
      );

      qc.setQueryData<{ id: number; isFavorite?: boolean | null }>(
        getGetRecipeQueryKey(recipeId),
        (old) => (old ? { ...old, isFavorite: next } : old)
      );
    },
    [qc, recipeId]
  );

  const onSettled = useCallback(() => {
    void qc.invalidateQueries({ queryKey: getListFavoritesQueryKey() });
  }, [qc]);

  const addMut = useAddFavorite({
    mutation: {
      onMutate: () => flipCachedFlag(true),
      onError: () => {
        flipCachedFlag(false);
        toast({
          title: "Could not add to favorites",
          description: "Please try again.",
          variant: "destructive",
        });
      },
      onSettled,
    },
  });

  const removeMut = useRemoveFavorite({
    mutation: {
      onMutate: () => flipCachedFlag(false),
      onError: () => {
        flipCachedFlag(true);
        toast({
          title: "Could not remove from favorites",
          description: "Please try again.",
          variant: "destructive",
        });
      },
      onSettled,
    },
  });

  const pending = addMut.isPending || removeMut.isPending;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Cards wrap the button in a <Link>; stop the click from navigating.
      e.preventDefault();
      e.stopPropagation();
      if (pending) return;
      if (isFavorite) {
        removeMut.mutate({ recipeId });
      } else {
        addMut.mutate({ recipeId });
      }
    },
    [isFavorite, pending, recipeId, addMut, removeMut]
  );

  const label = isFavorite ? "Remove from favorites" : "Add to favorites";

  if (variant === "detail") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={isFavorite}
        aria-label={label}
        title={label}
        disabled={pending}
        className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all",
          isFavorite
            ? "bg-rose-50 border-rose-300 text-rose-600 hover:bg-rose-100"
            : "bg-white border-border text-muted-foreground hover:border-rose-300 hover:text-rose-500",
          pending && "opacity-60 cursor-wait",
          className
        )}
      >
        <Heart
          className={cn("w-3.5 h-3.5", isFavorite && "fill-current")}
        />
        <span className="hidden sm:inline">
          {isFavorite ? "Favorited" : "Favorite"}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isFavorite}
      aria-label={label}
      title={label}
      disabled={pending}
      className={cn(
        "inline-flex items-center justify-center w-9 h-9 rounded-full backdrop-blur-sm shadow-sm border transition-all",
        isFavorite
          ? "bg-rose-500/95 border-rose-500 text-white hover:bg-rose-600"
          : "bg-white/85 border-white/50 text-muted-foreground hover:text-rose-500 hover:bg-white",
        pending && "opacity-70 cursor-wait",
        className
      )}
    >
      <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
    </button>
  );
}
